import type {EventItemInput, EventItemConverterOutput, EventProductItem, EventProductBase} from '~/types/event-item-converter'
import {useUserStore} from '@/store/user'
import jwt_decode from 'jwt-decode'

export const useEventItemConverter = () => {
  const mapProductsToEventItems = (products: EventProductBase[]): EventProductItem[] => {
    return products.map((product) =>
    {
      // Extract sku from articleNo, which is the first part of the string when split by ' '
      const sku = product.articleNo.split(' ')[0] || ''
      return {
        item_id: `SKU_${sku}`,
        item_name: sku || '',
        item_category: product.designname || product.categories?.[0] || '',
        item_category2: product.categories?.[1] || '',
        item_category3: product.categories?.[2] || '',
        item_category4: product.categories?.[3] || '',
        item_category5: product.categories?.[4] || '',
        item_brand: product.brand || '',
        item_variant: product.variant || '',
        location_id: ''
      }})
  }

  const convert = (input: EventItemInput): EventItemConverterOutput => {
    const result: any = {
      event: input.event
    }

    if (input.category) {
      result.category = input.category
    }
    if (input.action) {
      result.action = input.action
    }
    if (input.item_list_id) {
      result.item_list_id = input.item_list_id
    }
    if (input.item_list_name) {
      result.item_list_name = input.item_list_name
    }

    // Add personalization data if flag is true
    if (input.add_personalization) {
      try {
        const {accessToken} = storeToRefs(useUserStore())
        if (accessToken.value && accessToken.value !== '') {
          const decodedToken = jwt_decode(accessToken.value)

          result.userType = decodedToken?.userIsInternal ? 'internal' : 'external'
          result.salesArea = decodedToken?.salesAreaName ?? ''
          result.region = useNuxtApp().$globalization.getRegion(false) ?? ''
          result.customerNo = decodedToken?.companyNo ?? ''
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn('[GTM] Failed to add personalization data:', error)
      }
    }

    // Initialize all dynProp slots
    for (let i = 1; i <= 15; i++) {
      result[`dynProp${i}`] = ''
    }

    // Convert props to dynProp1-15
    const propEntries = Object.entries(input?.props || {})
    const maxProps = Math.min(propEntries.length, 15)

    for (let i = 0; i < maxProps; i++) {
      const [key, value] = propEntries[i]

      if (typeof value === 'object' && value !== null) {
        result[`dynProp${i + 1}`] = Object.entries(value)
          .map(([k, v]) => `${k}:${v}`)
          .join(';')
      } else {
        result[`dynProp${i + 1}`] = `${key}:${value}`
      }
    }

    // Ensure items array is properly formatted as EventProductItem[]
    result.ecommerce = {items: input.items || []}

    return result
  }

  return {convert, mapProductsToEventItems}
}
